import { actionTypes } from '@0xtorch/core'
import { z } from 'zod'

/** Actionを複合キーで参照するためのschema。 */
export const assistantActionKeySchema = z
  .object({
    sourceId: z.string().min(1),
    actionOrder: z.number().int().nonnegative(),
  })
  .strict()

/** nullable列をDBのnullへ戻す命令schema。 */
const clearFieldSchema = z.object({ operation: z.literal('clear') }).strict()

/**
 * 型ごとのset命令schemaを作る。
 * @param params set値のschema。
 * @returns set命令schema。
 */
const createSetFieldSchema = <T extends z.ZodTypeAny>({
  valueSchema,
}: {
  readonly valueSchema: T
}) =>
  z
    .object({
      operation: z.literal('set'),
      value: valueSchema,
    })
    .strict()

/**
 * setとclearを許すnullable列用schemaを作る。
 * @param params set値のschema。
 * @returns nullable列変更schema。
 */
const createNullableFieldChangeSchema = <T extends z.ZodTypeAny>({
  valueSchema,
}: {
  readonly valueSchema: T
}) =>
  z.discriminatedUnion('operation', [
    createSetFieldSchema({ valueSchema }),
    clearFieldSchema,
  ])

/** アシスタントが変更できるAction列だけを表すschema。 */
export const assistantActionChangeFieldsSchema = z
  .object({
    type: createSetFieldSchema({ valueSchema: z.enum(actionTypes) }).optional(),
    subType: createNullableFieldChangeSchema({
      valueSchema: z.number().int().nonnegative(),
    }).optional(),
    evidence: createSetFieldSchema({
      valueSchema: z.literal('assistant-check'),
    }).optional(),
    locked: createSetFieldSchema({
      valueSchema: z.literal(true),
    }).optional(),
    generatedTx: createSetFieldSchema({
      valueSchema: z.literal(false),
    }).optional(),
    comment: createNullableFieldChangeSchema({
      valueSchema: z.string(),
    }).optional(),
    loanId: createNullableFieldChangeSchema({
      valueSchema: z.string().min(1),
    }).optional(),
    crossId: createNullableFieldChangeSchema({
      valueSchema: z.string().min(1),
    }).optional(),
    crossType: createNullableFieldChangeSchema({
      valueSchema: z.enum(['start', 'middle', 'end']),
    }).optional(),
  })
  .strict()
  .refine((fields) => Object.keys(fields).length > 0, {
    message: 'assistant action change must contain at least one field',
  })

/** 1件のActionへ適用する許可済み変更のschema。 */
export const assistantActionChangeSchema = assistantActionKeySchema
  .extend({
    fields: assistantActionChangeFieldsSchema,
  })
  .strict()

/** 提案本文へ保存し、承認時にも再検証する共通変更payload schema。 */
export const assistantChangePayloadSchema = z
  .object({
    actions: z.array(assistantActionChangeSchema).min(1),
  })
  .strict()
  .superRefine((payload, context) => {
    const keys = new Set<string>()
    for (const action of payload.actions) {
      const key = JSON.stringify([action.sourceId, action.actionOrder])
      if (keys.has(key)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'assistant action change key must be unique',
        })
      }
      keys.add(key)
    }
  })

/** 拒否フィンガープリントの共通DB入力schema。 */
export const assistantRejectedFingerprintInputSchema = z
  .object({
    fingerprint: z.string().regex(/^[0-9a-f]{64}$/),
    ruleKey: z.string().min(1),
    ruleVersion: z.number().int().positive(),
    revalidationPayloadJson: z.string().min(1),
  })
  .strict()

/** Action複合キー。 */
export type AssistantActionKey = z.infer<typeof assistantActionKeySchema>

/** 許可済みAction変更。 */
export type AssistantActionChange = z.infer<typeof assistantActionChangeSchema>

/** 提案へ保存する共通変更payload。 */
export type AssistantChangePayload = z.infer<
  typeof assistantChangePayloadSchema
>

/** 拒否時にbrowserから渡す再検証済みfingerprint。 */
export type AssistantRejectedFingerprintInput = z.infer<
  typeof assistantRejectedFingerprintInputSchema
>

/**
 * JSON文字列を構文検証して値を返す。
 * @param params 検証対象のJSON文字列。
 * @returns JSON.parseで得た値。
 */
export const parseAssistantJson = ({
  json,
}: {
  readonly json: string
}): unknown => {
  try {
    return JSON.parse(json)
  } catch {
    throw new Error('invalid assistant JSON')
  }
}
